import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { useAuth, api, API } from "../App";
import AppShell from "../components/AppShell";
import {
  Copy,
  Check,
  RotateCcw,
  Calendar,
  RefreshCw,
  Sparkles,
  Download,
  ChevronLeft,
  ChevronRight,
  Star,
} from "lucide-react";
import { cleanPostContent } from "../utils/postContent";
import { TOPIC_LABELS, formatDate } from "../utils/constants";

const IMAGE_TYPE_LABELS = {
  infographic: "Infographic",
  carousel: "Carousel",
  dalle: "AI Image",
};

const HistoryCard = ({ post, user, onCopy, onRestore, onStar, copiedId, starredIds }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const content = cleanPostContent(post.variant_content);
  const shouldTruncate = content.length > 400;
  const displayContent = shouldTruncate && !isExpanded
    ? content.substring(0, 400)
    : content;

  const backendUrl = API.replace(/\/api$/, "");
  const imageUrl = post.image_path ? `${backendUrl}/static/images/${post.image_path}` : null;
  const carouselUrls = post.carousel_paths ? post.carousel_paths.map(p => `${backendUrl}/static/images/${p}`) : [];
  const hasImage = post.image_status === "done" && post.image_type && (imageUrl || carouselUrls.length > 0);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      if (post.image_type === "carousel") {
        const { jsPDF } = await import("jspdf");
        const PAGE_W = 216, PAGE_H = 270;
        const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: [PAGE_W, PAGE_H] });
        for (let i = 0; i < carouselUrls.length; i++) {
          if (i > 0) doc.addPage([PAGE_W, PAGE_H], "portrait");
          const resp = await fetch(carouselUrls[i]);
          if (!resp.ok) throw new Error(`Slide ${i + 1} failed`);
          const blob = await resp.blob();
          const dataUrl = await new Promise((res, rej) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.onerror = rej;
            r.readAsDataURL(blob);
          });
          doc.addImage(dataUrl, "PNG", 0, 0, PAGE_W, PAGE_H);
        }
        doc.save(`carousel_${post.id}.pdf`);
      } else {
        const resp = await fetch(imageUrl);
        if (!resp.ok) throw new Error("Download failed");
        const blob = await resp.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = imageUrl.split("/").pop() || `post_${post.id}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objUrl);
      }
      toast.success("Download started");
    } catch {
      toast.error("Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className="bg-card rounded-xl border border-border hover:border-primary/25 transition-all duration-200 overflow-hidden min-w-0"
      data-testid={`history-card-${post.id}`}
    >
      {/* Header */}
      <div className="flex items-start gap-3 px-4 pt-4 pb-2">
        {user?.picture ? (
          <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full shrink-0" />
        ) : (
          <div className="w-9 h-9 bg-gradient-to-br from-primary to-violet-600 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
            {user?.name?.charAt(0) || "U"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-headline font-bold text-sm text-foreground leading-tight">{user?.name || "You"}</p>
          <p className="text-xs text-muted-foreground leading-snug mt-0.5 truncate">
            {TOPIC_LABELS[post.topic] || post.topic}
          </p>
          {post.used_at && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {formatDate(post.used_at)}
            </p>
          )}
        </div>
        <button
          onClick={() => onStar(post)}
          data-testid={`star-btn-${post.id}`}
          title={starredIds?.has(post.id) ? "Remove from saved" : "Save post"}
          className={`p-1.5 rounded-lg transition-colors shrink-0 ${
            starredIds?.has(post.id)
              ? "text-amber-500 hover:bg-amber-500/10"
              : "text-muted-foreground hover:text-amber-500 hover:bg-muted"
          }`}
        >
          <Star className={`w-4 h-4 ${starredIds?.has(post.id) ? "fill-amber-500" : ""}`} />
        </button>
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        <p className="text-foreground whitespace-pre-wrap leading-relaxed text-sm break-words">
          {displayContent}
          {shouldTruncate && !isExpanded && (
            <button
              onClick={() => setIsExpanded(true)}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline ml-0.5 text-sm font-semibold"
            >
              {" "}see more
            </button>
          )}
        </p>
        {shouldTruncate && isExpanded && (
          <button
            onClick={() => setIsExpanded(false)}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline text-xs mt-1 font-semibold"
          >
            Show less
          </button>
        )}
      </div>

      {/* Takeaway */}
      {post.takeaway && (
        <div className="mx-4 mb-3 px-3 py-2.5 bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg flex gap-2.5 items-start">
          <Sparkles className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-widest mb-1">Takeaway</p>
            <p className="text-xs text-slate-800 dark:text-slate-200 leading-relaxed">{post.takeaway}</p>
          </div>
        </div>
      )}

      {/* Image Section */}
      {hasImage && (
        <div className="mt-1">
          {post.image_type !== "carousel" && imageUrl ? (
            <img
              src={imageUrl}
              alt={IMAGE_TYPE_LABELS[post.image_type] || "Post visual"}
              className="w-full border-y border-border object-cover"
            />
          ) : carouselUrls.length > 0 ? (
            <div className="relative">
              <img
                src={carouselUrls[carouselIdx]}
                alt={`Slide ${carouselIdx + 1} of ${carouselUrls.length}`}
                className="w-full border-y border-border object-cover"
              />
              {carouselUrls.length > 1 && (
                <>
                  <button
                    onClick={() => setCarouselIdx(i => Math.max(0, i - 1))}
                    disabled={carouselIdx === 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 disabled:opacity-30 transition-opacity"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCarouselIdx(i => Math.min(carouselUrls.length - 1, i + 1))}
                    disabled={carouselIdx === carouselUrls.length - 1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 disabled:opacity-30 transition-opacity"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <div className="flex justify-center gap-1.5 mt-2 pb-2">
                    {carouselUrls.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCarouselIdx(i)}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${i === carouselIdx ? "bg-primary" : "bg-muted-foreground/30"}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Action bar */}
      <div className="border-t border-border px-3 py-2.5 flex items-center gap-2">
        <button
          onClick={() => onCopy(post)}
          data-testid={`copy-btn-${post.id}`}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
            copiedId === post.id
              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "bg-indigo-600 text-white hover:bg-indigo-700"
          }`}
        >
          {copiedId === post.id ? (
            <><Check className="w-4 h-4" /> Copied!</>
          ) : (
            <><Copy className="w-4 h-4" /> Copy to LinkedIn</>
          )}
        </button>

        {hasImage && (
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            data-testid={`download-btn-${post.id}`}
            title={post.image_type === "carousel" ? "Download as PDF" : "Download PNG"}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-500/10 text-slate-600 dark:text-slate-400 hover:bg-slate-500/20 transition-colors shrink-0 disabled:opacity-50"
          >
            {isDownloading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          </button>
        )}

        <button
          onClick={() => onRestore(post)}
          data-testid={`restore-btn-${post.id}`}
          title="Restore to feed"
          className="w-9 h-9 flex items-center justify-center rounded-lg bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors shrink-0"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const HistorySkeleton = () => (
  <div className="bg-card rounded-xl border border-border overflow-hidden">
    <div className="flex items-start gap-3 px-4 pt-4 pb-2">
      <Skeleton className="w-9 h-9 rounded-full shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
    <div className="px-4 py-2 space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
    </div>
    <div className="border-t border-border mx-4" />
    <div className="flex gap-2 px-4 py-3">
      <Skeleton className="h-8 flex-1 rounded-lg" />
      <Skeleton className="h-8 flex-1 rounded-lg" />
    </div>
  </div>
);

const HistoryPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [starredIds, setStarredIds] = useState(new Set());

  const fetchHistory = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setIsLoading(true);
      else setIsLoadingMore(true);

      const response = await api.get(`/posts/history?page=${pageNum}&limit=10`);
      const newPosts = response.data.posts;

      if (append) {
        setPosts((prev) => [...prev, ...newPosts]);
      } else {
        setPosts(newPosts);
      }

      setHasMore(response.data.has_more);
      setTotal(response.data.total);
      setPage(pageNum);
    } catch (error) {
      console.error("Failed to fetch history:", error);
      toast.error("Failed to load history");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleCopy = async (post) => {
    try {
      await navigator.clipboard.writeText(cleanPostContent(post.variant_content));
      setCopiedId(post.id);
      toast.success("Post copied to clipboard!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
      toast.error("Failed to copy post");
    }
  };

  const handleRestore = async (post) => {
    try {
      await api.post(`/posts/${post.id}/unuse`);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      setTotal((prev) => prev - 1);
      toast.success("Post restored to feed");
    } catch (error) {
      console.error("Failed to restore:", error);
      toast.error("Failed to restore post");
    }
  };

  const handleStar = async (post) => {
    const isStarred = starredIds.has(post.id);
    try {
      if (isStarred) {
        await api.post(`/posts/${post.id}/unstar`);
        setStarredIds((prev) => { const next = new Set(prev); next.delete(post.id); return next; });
        toast.info("Removed from saved");
      } else {
        await api.post(`/posts/${post.id}/star`);
        setStarredIds((prev) => new Set(prev).add(post.id));
        toast.success("Saved!");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleLoadMore = () => {
    fetchHistory(page + 1, true);
  };

  return (
    <AppShell activeNav="history">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-xl font-bold text-foreground mb-6">Used Posts</h1>

        {/* Stats */}
        <div className="mb-8 p-4 bg-muted/50 rounded-xl">
          <p className="text-sm text-muted-foreground">
            You've used <span className="font-semibold text-foreground">{total}</span> posts so far.
            Great job staying consistent!
          </p>
        </div>

        {/* History List */}
        {isLoading ? (
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <HistorySkeleton key={i} />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No history yet</h3>
            <p className="text-muted-foreground mb-4">
              Posts you copy or mark as used will appear here.
            </p>
            <Button
              onClick={() => navigate("/feed")}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              Browse Posts
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-6">
              {posts.map((post) => (
                <HistoryCard
                  key={post.id}
                  post={post}
                  user={user}
                  onCopy={handleCopy}
                  onRestore={handleRestore}
                  onStar={handleStar}
                  copiedId={copiedId}
                  starredIds={starredIds}
                />
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  data-testid="load-more-btn"
                  className="px-8"
                >
                  {isLoadingMore ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load More"
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
};

export default HistoryPage;
